import React, { useRef, useEffect, useState } from "react";

export default function TextAreaEditor({ value, onChange, placeholder }) {
    const editorRef = useRef(null);
    const [activeFormats, setActiveFormats] = useState({
        bold: false,
        italic: false,
        underline: false,
    });

    // Apply formatting command
    const formatText = (command) => {
        document.execCommand(command, false, null);
        triggerChange();
        updateActiveFormats();
    };

    // Trigger synthetic onChange event
    const triggerChange = () => {
        if (!editorRef.current) return;
        const syntheticEvent = {
            target: { value: editorRef.current.innerHTML },
        };
        onChange(syntheticEvent);
    };

    // Update editor content from external value
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    // Detect which formatting options are active
    const updateActiveFormats = () => {
        if (!editorRef.current) return;
        setActiveFormats({
            bold: document.queryCommandState("bold"),
            italic: document.queryCommandState("italic"),
            underline: document.queryCommandState("underline"),
        });
    };

    // Listen to cursor and selection changes
    useEffect(() => {
        const handleSelectionChange = () => {
            if (document.activeElement === editorRef.current) {
                updateActiveFormats();
            }
        };

        document.addEventListener("selectionchange", handleSelectionChange);
        return () => {
            document.removeEventListener("selectionchange", handleSelectionChange);
        };
    }, []);

    return (
        <div className="border border-gray-300 rounded p-2 bg-white shadow-sm">
            {/* Toolbar */}
            <div className="flex space-x-2 mb-3 border-b border-gray-200 pb-2">
                {[
                    { cmd: "bold", label: "B", style: "font-bold" },
                    { cmd: "italic", label: "I", style: "italic" },
                    { cmd: "underline", label: "U", style: "underline" },
                ].map(({ cmd, label, style }) => (
                    <button
                        key={cmd}
                        type="button"
                        onClick={() => formatText(cmd)}
                        className={`w-8 h-8 flex items-center justify-center rounded border text-gray-700 border-gray-300 transition-colors
                            ${style}
                            ${activeFormats[cmd] ? "bg-gray-300 text-black" : "hover:bg-gray-100 active:bg-gray-200"}
                        `}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Editable content */}
            <div
                ref={editorRef}
                contentEditable
                className="min-h-[120px] px-3 py-2 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400"
                onInput={triggerChange}
                onClick={updateActiveFormats}
                onKeyUp={updateActiveFormats}
                data-placeholder={placeholder}
            />
        </div>
    );
}

